import { ReactNode } from "react"

import { DataTableEntityType } from "./data-view"
import type { SearchResult } from "./search"

export type ConditionType =
  | "Conditions::FormAnswerCondition"
  | "Conditions::StringCondition"
  | "Conditions::BooleanCondition"
  | "Conditions::DecimalCondition"
  | "Conditions::EnumCondition"
  | "Conditions::ArrayCondition"
  | "Conditions::DatetimeCondition"
  | "Conditions::IntegerCondition"
  | "Conditions::AgeCondition"
  | "Conditions::BirthdayCondition"
  | "Conditions::CustodianAccountLinkStatusCondition"
  | "Conditions::AssignedToUserCondition"
  | "Conditions::HasTranscriptCondition"
  | "Conditions::SSNPresenceCondition"
  | "Conditions::LinkedHouseholdCondition"
  | "Conditions::MeetingAssociationTypeCondition"
  | "Conditions::MeetingOwnerCondition"
  | "Conditions::MeetingTopicCondition"
  | "Conditions::EffectiveReviewCadenceCondition"
  | "Conditions::IntakeFormCountCondition"
  | "Conditions::IntakeFormActivityAtCondition"
  | "Conditions::IntakeFormInvitationStateCondition"
  | "Conditions::IntakeFormSubmissionStatusCondition"
  | "Conditions::IntakeFormSubmissionSourceCondition"
  | "Conditions::TaskRelationshipCondition"
  | "Conditions::ProjectRelatedRecordCondition"
  | "Conditions::CustomFieldRelationshipCondition"

export const VIEW_FILTER_OPERATORS = [
  "eq",
  "not_eq",
  "matches",
  "does_not_match",
  "starts_with",
  "ends_with",
  "in",
  "not_in",
  "is_blank",
  "is_not_blank",
  "lt",
  "gt",
  "recent_by",
  "gteq",
  "lteq",
  "contains",
  "does_not_contain",
  "overlaps",
  "not_overlaps",
  "contains_all",
  "relative",
  "age_between",
  "age_greater_than",
  "age_less_than",
  "age_equals",
  "upcoming_days",
  "is_today",
  "is_this_month",
  "is_in_month",
  "next_30_days",
  "between",
] as const

export const VIEW_FILTER_OPERATOR_LABELS = {
  eq: "is",
  not_eq: "is not",
  matches: "contains",
  does_not_match: "does not contain",
  starts_with: "starts with",
  ends_with: "ends with",
  in: "is one of",
  not_in: "is not one of",
  is_blank: "is blank",
  is_not_blank: "is not blank",
  lt: "less than",
  gt: "greater than",
  recent_by: "in the last",
  gteq: "greater than or equal to",
  lteq: "less than or equal to",
  contains: "contains",
  does_not_contain: "does not contain",
  overlaps: "has",
  not_overlaps: "does not have",
  contains_all: "has all of",
  age_between: "between",
  age_greater_than: "older than",
  age_less_than: "younger than",
  age_equals: "is exactly",
  upcoming_days: "in next",
  is_today: "is today",
  is_this_month: "is this month",
  is_in_month: "in month",
  next_30_days: "next 30 days",
  relative: "is",
  between: "between",
} as const

export const TAG_FILTER_OPERATOR_LABELS = {
  overlaps: "has any of",
  contains_all: "has all of",
  not_overlaps: "does not have any of",
} as const

export const NONVALUE_OPERATORS = ["is_blank", "is_not_blank", "is_today", "is_this_month", "next_30_days"]

export type ConditionOperator = (typeof VIEW_FILTER_OPERATORS)[number]

export interface Condition {
  id?: string
  entity_type: DataTableEntityType
  field?: string
  label?: string
  operator?: ConditionOperator
  value?: string
  values?: string[]
  sort_order?: number
  type?: ConditionType
  condition_group_id?: string
  _destroy?: boolean
  viewFilterOption?: ConditionOption
  installRequired?: boolean
}

export interface ConditionGroup {
  id?: string
  combinator: "and" | "or"
  data_view_id?: string
  conditions: Condition[]
  child_groups?: ConditionGroup[]
}

export type ConditionInputType =
  | "select"
  | "multi_select"
  | "text"
  | "number"
  | "date"
  | "date_range"
  | "boolean"
  | "none"
  | "relationship"

export interface ConditionValueOption {
  value: string
  label: string | ReactNode
  textLabel?: string
}

export interface ConditionOption {
  entity_type: DataTableEntityType | "person"
  field: string
  label: string
  operators: ConditionOperator[]
  defaultOperator: ConditionOperator
  inputType: ConditionInputType | Partial<Record<ConditionOperator, ConditionInputType>>
  isCurrency?: boolean
  type: ConditionType
  options?: ConditionValueOption[]
  placeholder?: string
  pinned?: boolean
  includeSelectAll?: boolean
  operatorLabels?: Partial<Record<ConditionOperator, string>>
  dependsOn?: { field: string; type: ConditionType }
  // Distinguishes same-key custom fields across entity types in picker identity.
  optionId?: string
  relationshipSearchTypes?: SearchResult["type"][]
  additionalCondition?: {
    connectorLabel?: string
    option: ConditionOption
    optionsByParentValue?: Record<string, ConditionValueOption[]>
  }
}
